import { EyeIcon } from '@heroicons/react/24/outline';
import {
  Download01Icon,
  GitBranch01Icon,
  Globe01Icon,
  HeartIcon,
  Lock01Icon,
  PlayCircleIcon,
} from '@langchain/untitled-ui-icons';
import { Button, Chip, LinearProgress, Stack } from '@mui/joy';
import Alert from '@mui/joy/Alert';
import Box from '@mui/joy/Box';

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import RelativeTime from '@/components/Hub/RelativeTime';
import Pagination from '@/components/Pagination';
import { Tooltip } from '@/design-system';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { usePagination } from '@/hooks/usePagination';
import { useOrganizationId, useRepos } from '@/hooks/useSwr';
import PromptIcon from '@/icons/PromptIcon.svg?react';
import { IsArchivedFilterOptions, RepoWithLookupsSchema } from '@/types/schema';
import { addQueryParams } from '@/utils/add-query-params';
import { appHubIndexPath } from '@/utils/constants';
import { descriptionToOneLine, getNumberWithSuffix } from '@/utils/hub';

import SortChips, { SortMode } from './Hub/SortChips';
import TagList from './Hub/TagList';

export function NoRepos() {
  return (
    <Box marginBottom={3}>
      <Alert variant="outlined">There are no prompts.</Alert>
    </Box>
  );
}

const RepoCard = ({
  repo,
  headerMode = 'tags',
}: {
  repo: RepoWithLookupsSchema;
  mutate: () => void;
  headerMode: 'tags' | 'is_public';
}) => {
  const organizationId = useOrganizationId();
  const navigate = useNavigate();

  const numLikes = getNumberWithSuffix(repo.num_likes);
  const numViews = getNumberWithSuffix(repo.num_views);
  const numDownloads = getNumberWithSuffix(repo.num_downloads);
  const numCommits = getNumberWithSuffix(repo.num_commits);

  const playgroundLinkBase = `/${appHubIndexPath}/${repo.full_name}/playground`;
  const playgroundLink = addQueryParams(playgroundLinkBase, {
    organizationId,
  });

  const repoLink = `/${appHubIndexPath}/${repo.full_name}${
    organizationId ? `?organizationId=${organizationId}` : ''
  }`;

  return (
    <div>
      <Link to={repoLink}>
        <div className="flex flex-row rounded-md border border-secondary p-4 transition-all hover:shadow-lg active:bg-secondary active:shadow-none">
          <Box flexGrow={1} display="flex" flexDirection="column" gap={1}>
            {headerMode == 'is_public' ? (
              repo.is_public ? (
                <Chip
                  variant={'soft'}
                  color="primary"
                  size="md"
                  sx={{ borderRadius: 4 }}
                  startDecorator={<Globe01Icon className="size-4" />}
                >
                  Public{repo.is_archived ? ' Archive' : ''}
                </Chip>
              ) : (
                <Chip
                  variant={'soft'}
                  color="neutral"
                  size="md"
                  sx={{ borderRadius: 4 }}
                  startDecorator={<Lock01Icon className="size-4" />}
                >
                  Private{repo.is_archived ? ' Archive' : ''}
                </Chip>
              )
            ) : (
              <TagList selectedTags={repo.tags} />
            )}

            <Stack direction="row" spacing={2}>
              <h4 className="text-lg font-medium">{repo.full_name}</h4>
            </Stack>
            {repo.description && (
              <div className="text-sm">
                {descriptionToOneLine(repo.description)}
              </div>
            )}
            <div className="flex flex-row items-center gap-2">
              <span className="flex items-center gap-1.5 text-xs">
                <PromptIcon className="h-4 w-4" />
                Prompt
              </span>

              <span>&bull;</span>

              <span className="text-xs">
                Updated <RelativeTime utcTime={repo.updated_at} />
              </span>

              <span>&bull;</span>

              <span className="flex items-center gap-1.5 text-xs font-medium text-brand-green-400">
                <HeartIcon className="size-4" /> {numLikes}
              </span>

              <span>&bull;</span>

              <span className="flex items-center gap-1.5 text-xs font-medium text-brand-green-400">
                <EyeIcon className="h-5 w-5" /> {numViews}
              </span>

              <span>&bull;</span>

              <span className="flex items-center gap-1.5 text-xs font-medium text-brand-green-400">
                <Download01Icon className="size-4" /> {numDownloads}
              </span>

              <span>&bull;</span>

              <span className="flex items-center gap-1.5 text-xs font-medium text-brand-green-400">
                <GitBranch01Icon className="size-4" /> {numCommits}
              </span>
            </div>
          </Box>
          <Box display="flex" flexGrow={0} gap={1} alignItems={'start'}>
            <Tooltip title="Open in Playground">
              <Button
                variant="outlined"
                color="neutral"
                size="sm"
                sx={{ paddingLeft: '0.5rem', paddingRight: '0.5rem' }}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(playgroundLink);
                }}
              >
                <PlayCircleIcon className="my-auto size-4" />
              </Button>
            </Tooltip>
          </Box>
        </div>
      </Link>
    </div>
  );
};

const HubRepoList = ({
  tenantHandle,
  searchTerm,
  tags,
  hasCommits,
  headerMode = 'tags',
  includeArchiveToggle = false,
}: {
  tenantHandle?: string;
  searchTerm?: string;
  tags?: string[];
  hasCommits?: boolean;
  headerMode?: 'tags' | 'is_public';
  includeArchiveToggle?: boolean;
}) => {
  const pageSize = 15;

  const [page, setPage] = usePagination();

  const { value: useExactSearchForPrompts } = useOrgConfig(
    OrgConfigs.use_exact_search_for_prompts
  );

  // cannot use relevance sort mode without searchTerm
  const [sortMode, setSortModeState] = useState<SortMode>(
    searchTerm && !useExactSearchForPrompts
      ? { field: 'relevance', direction: 'desc' }
      : {
          field: 'num_likes',
          direction: 'desc',
        }
  );
  const [archiveMode, setArchiveMode] =
    useState<IsArchivedFilterOptions>('false');

  const prevTags = useRef<string[] | undefined>();
  useEffect(() => {
    // set page to 0 whenever tags change
    if (prevTags.current !== undefined) setPage(0, { replace: true });
    prevTags.current = tags;
  }, [tags]);

  const setSortMode = (newMode: SortMode) => {
    if (
      newMode.field === sortMode.field &&
      newMode.direction === sortMode.direction
    )
      return;
    setSortModeState(newMode);
    setPage(0);
  };

  // cannot use relevance sort mode without searchTerm
  // so override in that case
  const innerSortMode: SortMode =
    sortMode.field === 'relevance' && (!searchTerm || useExactSearchForPrompts)
      ? { field: 'num_likes', direction: 'desc' }
      : sortMode;

  const {
    data: repos,
    isLoading,
    mutate,
    error,
  } = useRepos({
    tenant_handle: tenantHandle,
    query: searchTerm,
    tags,
    offset: page * pageSize,
    limit: pageSize,
    has_commits: hasCommits,
    sort_field: innerSortMode.field,
    sort_direction: innerSortMode.direction,
    is_archived: archiveMode,
    is_public: true,
  });

  const numPages = Math.ceil((repos?.total || 1) / pageSize);

  if (isLoading && !repos) {
    return <LinearProgress />;
  }

  if (error) {
    return <Alert color="danger">{error.message}</Alert>;
  }

  const repoList = repos?.repos?.map((repo) => (
    <RepoCard
      repo={repo}
      key={repo.id}
      mutate={mutate}
      headerMode={headerMode}
    />
  ));
  return (
    <div className={isLoading ? 'animate-pulse' : 'mb-8'}>
      <Stack spacing={2}>
        <SortChips
          sortMode={innerSortMode}
          setSortMode={setSortMode}
          showRelevance={!!searchTerm}
          archiveMode={archiveMode}
          setArchiveMode={includeArchiveToggle ? setArchiveMode : undefined}
        />

        {isLoading ? <LinearProgress /> : <div style={{ height: 6 }} />}
        <Stack spacing={2}>{repoList?.length ? repoList : <NoRepos />}</Stack>
        {isLoading ? <LinearProgress /> : <div style={{ height: 6 }} />}
        <Pagination
          count={numPages}
          page={page}
          onChange={(_, newPage) => setPage(newPage)}
        />
      </Stack>
    </div>
  );
};

export default HubRepoList;
